// §Admin audit-log — Activity panel in App Detail (owner side). Lists the app's durable event log
// (who did what, when) over GET /v1/apps/:appId/owner/__events (ownsApp-gated server-side; a non-owner
// gets an empty list, never a leak). The trail itself is recorded server-side (emitAuditEvent → the
// internal __events collection) on real lifecycle actions — member joins, invites, group-feature
// toggles, message edits/deletes, booking events. This panel only READS it. Honest loading / empty /
// error / unavailable states; no fabricated rows.
import { useCallback, useEffect, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Activity, RotateCw } from 'lucide-preact';
import { t } from '../lib/i18n';

interface EventRow {
  id?: string;
  eventType?: string;
  entityType?: string;
  entityId?: string;
  actorType?: string;
  actorId?: string;
  payload?: Record<string, unknown>;
  createdAt?: number;
}

type Load =
  | { s: 'loading' }
  | { s: 'ready'; items: EventRow[] }
  | { s: 'error'; code?: number }
  | { s: 'unavailable' };

// Human labels for the event types emitted server-side (app-events.ts). §S680:
// SERVER event types map to i18n KEYS; an unknown type falls back to a readable
// de-slugged form of the RAW value, so a new event type never renders broken.
const LABEL_KEYS: Record<string, string> = {
  'member.joined': 'mgmt.ev.memberJoined',
  'invite.created': 'mgmt.ev.inviteCreated',
  'invite.redeemed': 'mgmt.ev.inviteRedeemed',
  'multiuser.enabled': 'mgmt.ev.multiOn',
  'multiuser.disabled': 'mgmt.ev.multiOff',
  'message.edited': 'mgmt.ev.msgEdited',
  'message.deleted': 'mgmt.ev.msgDeleted',
  'booking.requested': 'mgmt.ev.bkRequested',
  'booking.confirmed': 'mgmt.ev.bkConfirmed',
  'booking.cancelled': 'mgmt.ev.bkCancelled',
  'booking.rescheduled': 'mgmt.ev.bkRescheduled',
};

function label(type?: string): string {
  if (type && LABEL_KEYS[type]) return t(LABEL_KEYS[type]);
  if (!type) return t('mgmt.al.fallback');
  return type.replace(/[._]/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

function whenLabel(ts?: number): string {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  const diff = Date.now() - ts;
  if (diff < 60_000) return t('mgmt.time.justNow');
  if (diff < 3_600_000) return t('mgmt.time.m', { n: Math.floor(diff / 60_000) });
  if (diff < 86_400_000) return t('mgmt.time.h', { n: Math.floor(diff / 3_600_000) });
  return d.toLocaleDateString();
}

// Actor ids are opaque end-user ids; show a short, non-identifying form (the owner sees "who" by id,
// never raw PII — we never log names/emails into the audit payload).
function actorLabel(a?: string): string {
  if (!a || a === '__system__') return t('mgmt.al.system');
  return a.length > 12 ? `${a.slice(0, 6)}…${a.slice(-3)}` : a;
}

export function ActivityLogPanel({ appId }: { appId: string }): JSX.Element {
  const [load, setLoad] = useState<Load>({ s: 'loading' });

  const fetchEvents = useCallback(() => {
    setLoad({ s: 'loading' });
    fetch(`/v1/apps/${encodeURIComponent(appId)}/owner/__events`, { credentials: 'include' })
      .then(async (r) => {
        if (r.status === 503) return setLoad({ s: 'unavailable' });
        if (!r.ok) return setLoad({ s: 'error', code: r.status });
        const data = (await r.json()) as { items?: EventRow[] };
        setLoad({ s: 'ready', items: Array.isArray(data.items) ? data.items : [] });
      })
      .catch(() => setLoad({ s: 'error' }));
  }, [appId]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  return (
    <section aria-label={t('mgmt.al.title')} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="pf-row">
        <span className="pf-mgmt-panel__t" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <Activity size={16} /> {t('mgmt.al.title')}
          {load.s === 'ready' && <span className="pf-badge" style={{ marginInlineStart: 6 }}>{load.items.length}</span>}
        </span>
        <button type="button" className="pf-btn pf-btn--sm" onClick={fetchEvents} disabled={load.s === 'loading'}>
          <RotateCw size={13} /> {t('mgmt.refresh')}
        </button>
      </div>

      {load.s === 'loading' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[0, 1, 2].map((i) => <div key={i} className="skeleton-shimmer" style={{ height: 44, borderRadius: 'var(--r-lg)', border: '1px solid var(--border)' }} />)}
        </div>
      )}

      {load.s === 'unavailable' && (
        <div className="pf-mgmt-panel"><span className="pf-mgmt-panel__body"><span className="pf-mgmt-panel__t">{t('mgmt.al.unavailableT')}</span><span className="pf-mgmt-panel__s">{t('mgmt.al.unavailableS')}</span></span></div>
      )}

      {load.s === 'error' && (
        <div className="pf-mgmt-panel">
          <span className="pf-mgmt-panel__body"><span className="pf-mgmt-panel__t">{t('mgmt.al.errT')}</span><span className="pf-mgmt-panel__s">{load.code === 401 ? t('mgmt.al.errAuth') : t('mgmt.al.errGeneric')}</span></span>
          <button type="button" className="pf-btn pf-btn--sm" onClick={fetchEvents}><RotateCw size={13} /> {t('common.retry')}</button>
        </div>
      )}

      {load.s === 'ready' && load.items.length === 0 && (
        <div className="pf-empty" style={{ marginTop: 2 }}>
          <span className="pf-bubble pf-mark-halo" aria-hidden="true"><Activity size={22} /></span>
          <p className="pf-empty__title">{t('mgmt.al.emptyT')}</p>
          <p className="pf-empty__lede">{t('mgmt.al.emptyS')}</p>
        </div>
      )}

      {load.s === 'ready' && load.items.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {load.items.map((e, i) => (
            <li key={e.id || i} className="pf-mgmt-panel" style={{ alignItems: 'center' }}>
              <span className="pf-mgmt-panel__body">
                <span className="pf-mgmt-panel__t">{label(e.eventType)}</span>
                <span className="pf-mgmt-panel__s">
                  {actorLabel(e.actorId)}
                  {e.entityType ? ` · ${e.entityType}` : ''}
                  {e.createdAt ? ` · ${whenLabel(e.createdAt)}` : ''}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
